import React, { useEffect, useMemo } from "react";


const BRAND = {
  white: "#ffffff",
  charcoal: "#0b0b0f",
  gray: "#6b7280",
  burgundy: "#800020",
  gold: "#b08d57",
  lilac: "#a78bfa", // sponsor-only
};

function Rule({ className = "" }) {
  return <div className={`h-px w-full bg-black/10 ${className}`} />;
}

function Box({ children, className = "" }) {
  return <div className={`border border-black/15 bg-white ${className}`}>{children}</div>;
}

function SafeIcon() {
  // Preview-safe icon (no asset import). Replace with your real logo icon in your app.
  return (
    <svg width="36" height="36" viewBox="0 0 64 64" aria-hidden="true">
      <rect x="6" y="6" width="52" height="52" fill="#0b0b0f" />
      <rect x="12" y="12" width="40" height="40" fill="#ffffff" />
      <path
        d="M18 42V22h10c7 0 12 4 12 10s-5 10-12 10H18zm6-5h4c4 0 7-2 7-5s-3-5-7-5h-4v10z"
        fill="#0b0b0f"
      />
    </svg>
  );
}

function SiteHeader() {
  return (
    <header className="sticky top-0 z-40 bg-white">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-5 sm:pt-6">
          <div className="flex items-center justify-between gap-3">
            <div className="hidden sm:flex items-center gap-4">
              <a
                href="/"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Music × Culture
              </a>
              <a
                href="/"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Art × Fashion
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="/ams"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                AMS
              </a>
              <a
                href="/login"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Login
              </a>
            </div>
          </div>

          <Rule className="mt-4" />

          <div className="grid grid-cols-1 items-end gap-3 py-4 sm:grid-cols-[1fr_auto_1fr]">
            <div className="hidden sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                Africa Loud Suite
              </div>
              <div className="mt-1 text-xs text-neutral-600">Culture intelligence → creative execution</div>
            </div>

            <a href="/" className="mx-auto flex items-center gap-3 text-center" aria-label="Go to Home">
              <SafeIcon />
              <div>
                <div className="font-serif text-4xl tracking-tight text-neutral-950 sm:text-5xl">Africa Loud</div>
                <div className="-mt-1 text-[11px] font-semibold tracking-[0.25em] uppercase text-neutral-600">
                  Editorial
                </div>
              </div>
            </a>

            <div className="hidden justify-self-end sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Weekly drops</div>
              <div className="mt-1 text-xs text-neutral-600">Signals · Cities · Strategy</div>
            </div>
          </div>

          <Rule />

          <nav className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div className="flex items-center gap-2">
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Home
              </a>
              <a
                href="/ams/culture-code"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-black text-white"
              >
                AMS · Culture Code
              </a>
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Africa Loud Mag
              </a>
            </div>

            <div className="hidden items-center gap-2 sm:flex">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Subscribe</div>
              <div className="h-3 w-px bg-black/15" />
              <div className="text-[11px] text-neutral-600">One email · one week</div>
            </div>
          </nav>

          <Rule />
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  const links = [
    { label: "Instagram", href: "https://instagram.com/africaloudmag", external: true },
    { label: "Twitter", href: "https://twitter.com/africaloudmag", external: true },
    { label: "TikTok", href: "https://tiktok.com/@africaloudmag", external: true },
    { label: "Terms", href: "/terms" },
    { label: "Privacy", href: "/terms" },
    { label: "Collab", href: "/collab" },
  ];

  return (
    <footer className="mt-12 bg-neutral-800 px-5 py-6">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-semibold text-white">Africa Loud</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                target={l.external ? "_blank" : undefined}
                rel={l.external ? "noopener noreferrer" : undefined}
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/80 hover:text-white"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
        <div className="mt-4 text-xs text-white/70">© 2026 Africa Loud</div>
      </div>
    </footer>
  );
}

function SponsorStrip() {
  return (
    <div className="mt-6">
      <Box className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Partner</div>
            <div className="mt-1 font-serif text-xl tracking-tight text-neutral-950">ON THIRD - Premium Luxury</div>
            <div className="mt-1 text-sm text-neutral-700">Shop the scent with a 10% off offer via the partner link.</div>
          </div>
          <a
            href="https://onthird.co.za/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center border border-black px-5 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-950 hover:bg-black hover:text-white"
            style={{ borderColor: BRAND.lilac }}
          >
            Shop the scent <span className="ml-2">→</span>
          </a>
        </div>
      </Box>
    </div>
  );
}

function stripLegacyHtml(html) {
  // Remove wrappers/head assets/scripts so legacy pages don’t hijack styling.
  let out = html || "";

  // Remove doctype/html/head/body tags entirely
  out = out
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<html[^>]*>/gi, "")
    .replace(/<\/html>/gi, "")
    .replace(/<head[\s\S]*?<\/head>/gi, "")
    .replace(/<body[^>]*>/gi, "")
    .replace(/<\/body>/gi, "");

  // Remove scripts/styles/links (common in pasted legacy pages)
  out = out
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<link[^>]*>/gi, "");

  // Kill on* handlers (onclick, onload, etc.) to avoid runtime errors
  out = out.replace(/\son\w+\s*=\s*"[^"]*"/gi, "");
  out = out.replace(/\son\w+\s*=\s*'[^']*'/gi, "");

  return out.trim();
}

function rewriteLegacyImages(html, prefix = "/images/articles/") {
  // /images/foo.png -> /images/articles/foo.png
  return (html || "").replace(/(src=\s*["'])\/?images\//gi, `$1${prefix}`);
}

function normalizeAnchors(html) {
  // If you have legacy links like /echo/articles/x.html, you can remap them here.
  // Current behavior: keep links as-is.
  return html || "";
}

function ArticlePageTemplate({
  seoTitle,
  seoDescription,
  canonicalPath,
  heroTitle,
  heroByline,
  heroImage,
  legacyHtml,
  stripStyles = true,
  rewriteImages = true,
  sectionLabel = "AMS · Culture Code",
}) {
  const preparedHtml = useMemo(() => {
    let out = legacyHtml || "";
    if (stripStyles) out = stripLegacyHtml(out);
    if (rewriteImages) out = rewriteLegacyImages(out);
    out = normalizeAnchors(out);
    return out;
  }, [legacyHtml, stripStyles, rewriteImages]);

  useEffect(() => {
    // Minimal SEO in preview: update title/description.
    if (typeof document === "undefined") return;
    if (seoTitle) document.title = seoTitle;

    if (seoDescription) {
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "description");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", seoDescription);
    }

    // Canonical
    if (canonicalPath) {
      let canon = document.querySelector('link[rel="canonical"]');
      if (!canon) {
        canon = document.createElement("link");
        canon.setAttribute("rel", "canonical");
        document.head.appendChild(canon);
      }
      canon.setAttribute("href", canonicalPath);
    }
  }, [seoTitle, seoDescription, canonicalPath]);

  return (
    <div className="min-h-screen bg-white text-neutral-950">
      <SiteHeader />

      <main className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-8">
          <div className="flex items-center gap-3">
            <div className="h-2 w-2" style={{ backgroundColor: BRAND.burgundy }} />
            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-neutral-600">{sectionLabel}</div>
          </div>

          {/* Hero */}
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <Box className="p-6 sm:p-8">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Culture Pattern</div>
              <h1 className="mt-4 font-serif text-4xl leading-[1.05] tracking-tight sm:text-6xl">{heroTitle}</h1>
              {heroByline ? <div className="mt-3 text-sm text-neutral-600">{heroByline}</div> : null}
              <div className="mt-6">
                <Rule />
                <div className="mt-3 h-1 w-full" style={{ backgroundColor: BRAND.burgundy }} />
              </div>

              {/* Quick tags (AMS-style) */}
              <div className="mt-5 flex flex-wrap gap-2">
                <span className="border border-black/15 bg-black/5 px-3 py-2 text-[11px] font-semibold tracking-[0.16em] uppercase">
                  City Adoption
                </span>
                <span className="border border-black/15 bg-black/5 px-3 py-2 text-[11px] font-semibold tracking-[0.16em] uppercase">
                  Youth Acceleration
                </span>
                <span className="border border-black/15 bg-black/5 px-3 py-2 text-[11px] font-semibold tracking-[0.16em] uppercase">
                  Digital Amplification
                </span>
              </div>
            </Box>

            <Box className="relative overflow-hidden">
              <div
                className="h-full min-h-[280px] w-full"
                style={{
                  backgroundImage: `url('${heroImage}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div className="absolute bottom-0 left-0 right-0">
                <div className="bg-white/92 p-5 backdrop-blur-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Use</div>
                      <div className="mt-1 text-base font-semibold text-neutral-950">AMS</div>
                    </div>
                    <div className="text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: BRAND.burgundy }}>
                      Culture Code
                    </div>
                  </div>
                </div>
              </div>
            </Box>
          </div>

          {/* Sponsor stays on all pages */}
          <SponsorStrip />

          {/* Article body */}
          <div className="mt-8 pb-12">
            <Box className="p-6 sm:p-10">
              <article
                className="max-w-none text-neutral-900 leading-relaxed [&_p]:mb-5 [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-serif [&_h2]:text-3xl [&_h2]:tracking-tight [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-serif [&_h3]:text-2xl [&_h3]:tracking-tight [&_ul]:my-5 [&_ul]:pl-6 [&_li]:mb-2 [&_blockquote]:my-6 [&_blockquote]:border-l-4 [&_blockquote]:border-black/15 [&_blockquote]:pl-4 [&_blockquote]:text-neutral-700 [&_a]:underline [&_a]:decoration-black/30 hover:[&_a]:decoration-black"
                dangerouslySetInnerHTML={{ __html: preparedHtml }}
              />
            </Box>

            {/* AMS CTA (kept subtle) */}
            <div className="mt-6">
              <Box className="p-6 sm:p-8">
                <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                  Apply this lens
                </div>
                <div className="mt-2 font-serif text-2xl tracking-tight text-neutral-950">
                  Turn cultural movement into decision-ready strategy.
                </div>
                <p className="mt-3 text-sm text-neutral-700 max-w-3xl">
                  AMS Culture Code documents how sound becomes behaviour, how behaviour becomes adoption, and how adoption
                  becomes market outcomes — so teams can reduce risk and move with clarity.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <a
                    href="/ams"
                    className="inline-flex items-center justify-center border border-black bg-black px-5 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-white hover:bg-neutral-900"
                  >
                    Explore AMS <span className="ml-2">→</span>
                  </a>
                  <a
                    href="/ams/culture-code"
                    className="inline-flex items-center justify-center border border-black px-5 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-950 hover:bg-black hover:text-white"
                    style={{ borderColor: "rgba(0,0,0,0.6)" }}
                  >
                    More Culture Code <span className="ml-2">→</span>
                  </a>
                </div>
              </Box>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

// --- “Tests” (runs in dev/preview) ---
function runTemplateSanityTests() {
  // eslint-disable-next-line no-console
  console.assert(stripLegacyHtml("<script>alert(1)</script><p>ok</p>").includes("<p>ok</p>"), "stripLegacyHtml should remove script");
  // eslint-disable-next-line no-console
  console.assert(!stripLegacyHtml("<style>h1{}</style><p>ok</p>").includes("<style"), "stripLegacyHtml should remove style");
  // eslint-disable-next-line no-console
  console.assert(
    rewriteLegacyImages('<img src="/images/a.png" />').includes('/images/articles/a.png'),
    "rewriteLegacyImages should rewrite /images/"
  );
}

/**
 * EXPORT: AMS Culture Code article page
 * Route suggestion:
 * /ams/culture-code/amapiano-the-sound-that-built-a-generation
 */
export default function AmapianoCultureCodeArticle() {
  useEffect(() => {
    if (typeof window !== "undefined") runTemplateSanityTests();
  }, []);

  return (
    <ArticlePageTemplate
      seoTitle="Amapiano: The Sound That Built a Generation | AMS Culture Code"
      seoDescription="How Amapiano scaled through youth networks, platforms, and cross-border stages — and what it teaches brands about cultural adoption."
      canonicalPath="/ams/culture-code/amapiano-the-sound-that-built-a-generation"
      heroTitle="Amapiano: The Sound That Built a Generation"
      
heroByline="Written by Olwethu Bebeza • Africa Loud Suite • AMS Culture Code"

      heroImage="/images/articles/amapiano-hero.jpg"
      stripStyles={true}
      rewriteImages={true}
      legacyHtml={`
<div id="main-contents">

  <h2>Why Amapiano Matters</h2>
  <p>
    Amapiano did not just rise. It restructured youth visibility in Africa.
  </p>

  <p>
    Born in South Africa’s townships around 2015, the genre arrived quietly. Deep log drums. Airy keys.
    Slow-burning tempo. It felt local. Intimate. Community-driven.
  </p>

  <p>
    Then it accelerated.
  </p>

  <p>
    What followed was not just a music trend but a cultural shift. New artists emerged. New dance languages formed.
    Youth networks mobilised around a shared sound. Amapiano became more than music. It became infrastructure.
  </p>

  <h2>A Youth Pathway: From Bedroom to Stage</h2>
  <p>
    Amanda’s story reflects this shift.
  </p>

  <p>
    Raised in a home shaped by Jazz, RnB and Gospel, she grew up within a traditional listening culture.
    As a teenager, she discovered Kwaito, Hip Hop and House. When Amapiano surfaced, it felt like a continuation
    but also a disruption.
  </p>

  <p>
    She began creating dance routines at home, recording them on her phone. When Amapiano took over TikTok in South Africa,
    her living room became a stage.
  </p>

  <p>
    As the sound travelled, so did she.
  </p>

  <p>
    Dance challenges amplified exposure. Algorithms rewarded consistency. Youth audiences validated what felt authentic.
    What began as experimentation turned into recognition.
  </p>

  <p>
    A message arrived from a well-known Amapiano artist inviting her to feature in a music video.
    She assumed it was a scam. It wasn’t.
  </p>

  <p>
    From there, momentum compounded. Performances followed. Collaborations expanded beyond South Africa into Nigeria
    and other African markets. Her digital presence translated into physical stages.
  </p>

  <h2>The Adoption Pattern</h2>
  <blockquote>
    Cultural validation → Network amplification → Commercial mainstreaming
  </blockquote>

  <p>
    For a generation of African youth, the pattern became clear. The genre created access points into visibility,
    income and cross-border collaboration. It demonstrated how digital platforms accelerate local culture into
    continental influence.
  </p>

  <h2>What This Teaches Brands</h2>
  <p>
    Amapiano is a case study in how African culture scales. If you want to engage youth markets, you cannot skip the
    validation phase. You have to understand where the sound lives, who carries it, and what signals “real” in the network.
  </p>

  <ul>
    <li><strong>Start where culture is built:</strong> local scenes, creators, dance ecosystems, and informal circuits.</li>
    <li><strong>Track amplification pathways:</strong> challenges, duets, remixes, micro-influencers, and city-to-city spread.</li>
    <li><strong>Only then scale:</strong> partnerships, campaigns, and product integrations that don’t dilute the culture.</li>
  </ul>

  <p>
    Today Amapiano is global. It appears on international festival lineups. It charts beyond Africa. It shapes fashion,
    dance and nightlife ecosystems.
  </p>

  <p>
    But its most significant impact remains closer to home.
  </p>

  <p>
    It proved that when African youth culture moves, it does not wait for permission. It builds its own system.
  </p>

  <p>
    Amapiano is not just the future. It is a live blueprint for how youth culture turns into market reality.
  </p>

</div>
      `}
    />
  );
}
